import React from "react";
import { matchPath } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import GroupsIcon from "@mui/icons-material/Groups";
import WorkIcon from "@mui/icons-material/Work";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SettingsIcon from "@mui/icons-material/Settings";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

/**
 * Single source of truth for the sidebar, the quick search and the account menu.
 *
 * Visibility is NOT decided here: the server already filters `/adminarea/routing` by the user's
 * permission rules, so an item is shown only when its `path` is present among the routes the
 * server returned. `buildMenu` applies this configuration to that list.
 *
 * MenuItem  { label, path, activePaths? }   activePaths: extra route patterns (detail/edit pages)
 *                                            that keep this item highlighted.
 * MenuGroup { id, label, icon, path?, children? }   a group with `path` and no children is a
 *                                                    single entry (e.g. the dashboard).
 */
export const MENU_GROUPS = [
  { id: "dashboard", label: "Dashboard", icon: <DashboardIcon />, path: "/home" },
  {
    id: "people",
    label: "Persone",
    icon: <GroupsIcon />,
    children: [
      { label: "Aziende", path: "/company/list", activePaths: ["/company/edit/:id"] },
      { label: "Lista dipendenti", path: "/employee/list", activePaths: ["/employee/edit/:id", "/reward/edit/:id", "/reward/edit/:id/:quoteId/:employeeId"] },
      { label: "Ore registrate", path: "/employee/time-log" },
      { label: "Presenze", path: "/employee/attendance" },
      { label: "Attività dipendenti", path: "/employee-activities/list" },
    ],
  },
  {
    id: "projects",
    label: "Progetti",
    icon: <WorkIcon />,
    children: [
      { label: "Commesse", path: "/quote/list", activePaths: ["/quote/edit/:id"] },
      { label: "Stato commesse", path: "/ai/status/list", activePaths: ["/ai/status/detail/:id"] },
      { label: "Report progetti", path: "/project-report/list", activePaths: ["/project-report/edit/:id", "/project-report/filters/list", "/project-report/filters/edit/:id"] },
    ],
  },
  {
    id: "administration",
    label: "Amministrazione",
    icon: <ReceiptLongIcon />,
    children: [
      { label: "Fatture", path: "/invoice/list", activePaths: ["/invoice/report", "/invoice/export", "/invoice/list-reminder", "/document/edit/:id", "/document/create/invoice"] },
      { label: "Incassi", path: "/invoice-payment/list" },
      { label: "Spese", path: "/expenses/list", activePaths: ["/document/create/expenses"] },
      { label: "Movimenti bancari", path: "/bank-transaction/list" },
      { label: "Tesoreria", path: "/cashflow/dashboard", activePaths: ["/cashflow/categories"] },
      { label: "Report amministrazione", path: "/reports", activePaths: ["/report/quote", "/report/tag-quotes", "/report/time-materials"] },
    ],
  },
  {
    id: "cars",
    label: "Auto",
    icon: <DirectionsCarIcon />,
    children: [
      { label: "Auto", path: "/car-refunds/car/list", activePaths: ["/car-refunds/car/edit/:id", "/car-refunds/car-cost/edit/:id", "/car-refunds/car-cost/edit/:id/:car"] },
      { label: "Rimborsi auto", path: "/car-refunds/refund/list", activePaths: ["/car-refunds/refund/edit/:id"] },
      { label: "Uffici", path: "/car-refunds/offices" },
    ],
  },
  {
    id: "balances",
    label: "Bilanci",
    icon: <AccountBalanceIcon />,
    children: [
      { label: "Dashboard", path: "/bilanci/dashboard" },
      { label: "Andamento anno corrente", path: "/bilanci/andamento" },
      { label: "Archivio", path: "/bilanci", activePaths: ["/bilanci/visualizza/:id", "/bilanci/importa/:year?"] },
      { label: "Mappatura categorie", path: "/bilanci/mappatura" },
    ],
  },
  {
    id: "ai",
    label: "AI",
    icon: <SmartToyIcon />,
    children: [
      { label: "Template prompt", path: "/ai/prompt-template/list", activePaths: ["/ai/prompt-template/edit/:id"] },
      { label: "Configurazione agenti", path: "/ai/task-config/list", activePaths: ["/ai/task-config/edit/:id"] },
      { label: "Esecuzioni agenti", path: "/ai/task-run/list", activePaths: ["/ai/task-run/detail/:id"] },
      { label: "Prezzi modelli", path: "/ai/model-price/list", activePaths: ["/ai/model-price/edit/:id"] },
    ],
  },
  {
    id: "system",
    label: "Sistema",
    icon: <SettingsIcon />,
    children: [
      { label: "Utenti", path: "/user/list", activePaths: ["/user/edit/:id"] },
      { label: "Ruoli", path: "/role/list", activePaths: ["/role/edit/:id"] },
      { label: "Configurazioni", path: "/configuration/edit" },
      { label: "Cron", path: "/cron/list" },
    ],
  },
];

export const SYSTEM_GROUP_ID = "system";

// Routes the server allows but this configuration does not know yet land here, so a new page
// is never silently unreachable from the menu.
const FALLBACK_GROUP = { id: "other", label: "Altro", icon: <MoreHorizIcon /> };

const asArray = (value) => (Array.isArray(value) ? value : [value]);

// Menu entries of the server catalogue (already filtered by permissions) keyed by url.
const allowedEntries = (routes) => {
  const entries = new Map();
  (routes || []).forEach((group) => (group.items || []).forEach((item) => {
    if (!item.in_menu) return;
    asArray(item.url).forEach((url) => entries.set(url, { label: item.label, url }));
  }));
  return entries;
};

/**
 * Applies the configuration to the routes returned by the server. Groups keep only the children
 * the user may see; a group left without children disappears; allowed routes unknown to the
 * configuration are listed under "Altro".
 */
export const buildMenu = (routes) => {
  if (!routes) return [];
  const allowed = allowedEntries(routes);
  const used = new Set();
  const groups = [];
  MENU_GROUPS.forEach((group) => {
    if (group.path) {
      if (!allowed.has(group.path)) return;
      used.add(group.path);
      groups.push({ ...group, children: [] });
      return;
    }
    const children = (group.children || []).filter((child) => allowed.has(child.path));
    children.forEach((child) => used.add(child.path));
    if (children.length > 0) groups.push({ ...group, children });
  });
  const leftovers = Array.from(allowed.values()).filter((entry) => !used.has(entry.url));
  if (leftovers.length > 0) {
    groups.push({ ...FALLBACK_GROUP, children: leftovers.map((entry) => ({ label: entry.label || entry.url, path: entry.url })) });
  }
  return groups;
};

const matches = (pattern, pathname) => Boolean(matchPath(pathname, { path: pattern, exact: true }));

// Whether a menu entry is the one for `pathname`: exact match on its path or on its detail routes.
export const isItemActive = (item, pathname) =>
  matches(item.path, pathname) || (item.activePaths || []).some((pattern) => matches(pattern, pathname));

// The active entry and its group for the current location, or null.
export const findActive = (groups, pathname) => {
  for (const group of groups) {
    if (group.path && isItemActive(group, pathname)) return { groupId: group.id, path: group.path };
    for (const child of group.children || []) {
      if (isItemActive(child, pathname)) return { groupId: group.id, path: child.path };
    }
  }
  return null;
};

// Every reachable entry with the name of its group, for the quick search and the account menu.
export const flattenMenu = (groups) => groups.flatMap((group) => (
  group.path
    ? [{ label: group.label, path: group.path, group: group.label, groupId: group.id }]
    : (group.children || []).map((child) => ({ label: child.label, path: child.path, group: group.label, groupId: group.id }))
));

// Case- and accent-insensitive text for the search filter.
export const normalizeText = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[̀-ͯ]/g, "")
  .toLowerCase()
  .trim();

export const searchMenu = (groups, query) => {
  const needle = normalizeText(query);
  const entries = flattenMenu(groups);
  if (!needle) return entries;
  return entries.filter((entry) => normalizeText(`${entry.label} ${entry.group}`).includes(needle));
};

// localStorage access that never throws (private mode, disabled storage, quota).
export const readStorage = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch (_error) {
    return fallback;
  }
};

export const writeStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_error) {
    // The preference is simply not remembered.
  }
};

export const STORAGE_OPEN_GROUPS = "menu.openGroups";
export const STORAGE_MINI = "menu.mini";

export const DRAWER_WIDTH = 240;
export const DRAWER_MINI_WIDTH = 64;
